import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

export interface ClientErrorDto {
  message: string;
  stackTrace: string;
  timestamp: Date;
  url: string;
  route?: string;
  component?: string;
  action?: string;
  userId?: string;
  userAgent: string;
  level: 'Error' | 'Warning' | 'Info';
  additionalData?: any;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: any): void {
    // Get HttpClient from injector to avoid circular dependency
    const http = this.injector.get(HttpClient);
    const router = this.injector.get(Router);

    // Extract error information
    const errorData: ClientErrorDto = {
      message: this.getErrorMessage(error),
      stackTrace: error.stack || '',
      timestamp: new Date(),
      url: window.location.href,
      route: router.url,
      component: this.getComponentName(error),
      action: this.getAction(error),
      userId: this.getUserId()?.toString(),
      userAgent: navigator.userAgent,
      level: 'Error',
      additionalData: error.additionalData ? JSON.stringify(error.additionalData) : undefined
    };

    // Send to backend (don't block the UI)
    http.post(`${environment.apiUrl}/logs/client-error`, errorData)
      .subscribe({
        next: () => console.log('Error logged to server'),
        error: (err) => console.error('Failed to log error to server:', err)
      });

    // Also log to console for development
    if (!environment.production) {
      console.error('🔴 Global Error Handler caught:', error);
      console.error('Error data sent to server:', errorData);
    }

    // Re-throw in development for debugging
    if (!environment.production) {
      throw error;
    }
  }

  private getErrorMessage(error: any): string {
    if (error.message) {
      return error.message;
    }
    if (error.error?.message) {
      return error.error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  }

  private getComponentName(error: any): string {
    // Try to extract component name from stack trace
    const stackLines = error.stack?.split('\n') || [];
    for (const line of stackLines) {
      const match = line.match(/at (\w+Component)/);
      if (match) {
        return match[1];
      }
    }
    return 'Unknown';
  }

  private getAction(error: any): string {
    // Try to extract method name from stack trace
    const stackLines = error.stack?.split('\n') || [];
    if (stackLines.length > 1) {
      const match = stackLines[1].match(/at (?:\w+\.)?(\w+)/);
      if (match) {
        return match[1];
      }
    }
    return 'Unknown';
  }

  private getUserId(): string | undefined {
    try {
      const currentUser = localStorage.getItem('currentUser');
      if (currentUser) {
        const user = JSON.parse(currentUser);
        return user.id?.toString();
      }
    } catch {
      // Ignore errors when getting user ID
    }
    return undefined;
  }
}
